"use client";

import React from "react";
import { FiChevronRight, FiDownload } from "react-icons/fi";
import { usePwaInstall } from "./PwaInstallProvider";

export type InstallAppActionVariant = "outline" | "sidebar" | "row";

export type InstallAppActionProps = {
  /**
   * `outline`: bloco da confirmação e `/{slug}/app`.
   * `sidebar`: menu escuro do admin, no estilo do botão "Sair".
   * `row`: linha do perfil do cliente, com descrição e chevron.
   */
  variant: InstallAppActionVariant;
  label?: string;
  /** Só na variante `row`. */
  description?: string;
  className?: string;
};

const FOCUS_RING = "focus:outline-none focus:ring-4 focus:ring-primary-100";

/**
 * Botão reutilizável de instalação: prompt nativo, se existir; senão abre a
 * folha de passos (MENU-03, CONV-04). Some quando o app já está instalado ou
 * quando o aparelho não tem caminho de instalação.
 */
export function InstallAppAction({
  variant,
  label = "Instalar app",
  description,
  className = "",
}: InstallAppActionProps) {
  const { isInstalled, hasInstallPath, requestInstall } = usePwaInstall();

  if (isInstalled || !hasInstallPath) return null;

  const handleClick = () => {
    void requestInstall();
  };

  if (variant === "sidebar") {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold text-white/60 transition duration-150 hover:bg-white/10 hover:text-white ${FOCUS_RING} ${className}`}
      >
        <span className="flex h-5 w-5 items-center justify-center text-white/50">
          <FiDownload aria-hidden="true" />
        </span>
        <span>{label}</span>
      </button>
    );
  }

  if (variant === "row") {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition duration-150 hover:bg-slate-50 ${FOCUS_RING} ${className}`}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
          <FiDownload className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-900">
            {label}
          </span>
          {description ? (
            <span className="block truncate text-xs text-slate-500">{description}</span>
          ) : null}
        </span>
        <FiChevronRight className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition duration-150 hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700 ${FOCUS_RING} ${className}`}
    >
      <FiDownload className="h-4 w-4" aria-hidden="true" />
      {label}
    </button>
  );
}
